import _ from 'lodash'
import { useEffect, useMemo } from 'react'
import { useLocation, withRouter, RouteComponentProps } from 'react-router-dom'
import { getInitSettings, hasMicroApp } from '../../framework/states'
import { setValuesInternal } from '../../framework/actions'
import { SPARKY_FRAMEWORK } from '../../framework/constants'
import { getFrameworkVar } from '../../framework/states'
import { ensureArray } from '../../framework/utils'
import handleLogin, { getDefaultRoute } from './handleLogin'
import RouteViewer from './RouteViewer'
import { ComponentWrapper, MemoComponentWrapper } from './RouteWrapper'
import LeftNav from './LeftNav'
import LoadingPlaceholder from '../common/LoadingPlaceholder'
import { useActiveItems, useNavDisplayItems, useReadyToRenderAndClearMask, usePendoIdentity } from './navUtils'
import type { VFC, ReactElement } from 'react'
import type EventEmitter from 'eventemitter3'
import type { MainState, StoreState, Dispatch, OidcState, StateRef } from '../../framework/stateTypes'
import type { NavItem as NavItemType } from '../../framework/microAppTypes'

type MainProps = {
  state: StoreState
  main: MainState | undefined
  oidc: OidcState | undefined
  getState: () => StoreState
  stateRef: StateRef
  dispatch: Dispatch
  events: EventEmitter
}
const Main: VFC<MainProps & RouteComponentProps> = ({ state, main, oidc, getState, stateRef, dispatch, events }): ReactElement => {
  const location = useLocation()
  const { navRoutes, _showSideMenu, auth, leftMainRoutes, leftMainNavigation, leftMainActions } = state
  const { tenant, loggedInUser = auth?.user, licenseInfo, featuresInfo, tokenInfo } = main || {}
  const lite = Boolean(getInitSettings().lite)

  const hideSideMenu = () => {
    setValuesInternal({
      isSideNavCollapsed: false,
      _showSideMenu: false,
    })
  }

  const vars = useMemo(() => {
    return state.vars?.[SPARKY_FRAMEWORK] || {}
  }, [state.vars])

  const leftNavigation = useMemo(() => {
    let overriddenNav = leftMainNavigation?.reduce((acc: NavItemType[] | NavItemType, item: NavItemType) => {
      if (item.overrideLeftNav?.match) {
        const matchValue = item[item.overrideLeftNav.match]
        if (matchValue && item.overrideLeftNav.match === 'path' && location?.pathname?.startsWith(matchValue)) {
          const overrideItems = ensureArray<NavItemType>(item.overrideLeftNav.with ? item[item.overrideLeftNav.with] : item)
          const compDefaults = {} as Partial<NavItemType>
          if (item?.component) {
            compDefaults.component = item.component
            if (item.contentClassName) {
              compDefaults.contentClassName = item.contentClassName
            }
          }
          const needDefaultComp: ((child: NavItemType) => boolean) = !item.path || item.path.startsWith('#') ? () => true :
            (child) => !child.path?.startsWith(item.path!)
          acc.push(...overrideItems.map(child => {
            if (compDefaults.component && !child.component && needDefaultComp(child)) {
              return { ...compDefaults, ...child } // copy component from parent if path not matched
            }
            return { ...child }
          }))
          if (item.overrideLeftNav.logo) {
            (acc as NavItemType).logo = item.overrideLeftNav.logo
          }
          if (item.overrideLeftNav.collapsedLogo) {
            (acc as NavItemType).collapsedLogo = item.overrideLeftNav.collapsedLogo
          }
        }
      }
      return acc
    }, [])
    if (!overriddenNav?.length) {
      overriddenNav = leftMainNavigation
      if (getFrameworkVar('show_approved_nav_only') && hasMicroApp('sparky-one-nav') && overriddenNav?.length) {
        const [approved, nonapproved] = _.partition(overriddenNav, (item) =>
          item.__approved_item || item.hidden || item.overrideLeftNav
        )
        overriddenNav = approved
        if (nonapproved.length) {
          console.error('non-approved nav items!', nonapproved)
        }
      }
    }
    const leftNav = overriddenNav?.map((nav: NavItemType) => {
      if (nav.path !== '#' && Array.isArray(nav.children) && nav.children.length) {
        nav.children.forEach((child: NavItemType) => {
          if (child.path && nav?.path && !child.path.startsWith('#') && !child?.path?.startsWith('/')) {
            child.path = `${nav.path}/${child.path}`
          }
        })
        return {
          ...nav,
          alternateKey: nav.alternateKey || nav.path,
        }
      }
      return nav
    })
    const logo = (overriddenNav as NavItemType)?.logo
    const logoUrlRegex = /^(?:https?:|data:|\/)/
    if (logo && logoUrlRegex.test(logo)) {
      leftNav.logo = logo
    }
    const collapsedLogo = (overriddenNav as NavItemType)?.collapsedLogo
    if (collapsedLogo && logoUrlRegex.test(collapsedLogo)) {
      leftNav.collapsedLogo = collapsedLogo
    }
    return leftNav
  }, [location?.pathname, leftMainNavigation])

  const [logo, collapsedLogo] = useMemo(() => {
    const { logo = vars.logo, collapsedLogo = vars.collapsed_logo } = leftNavigation
    return [logo as string | undefined, collapsedLogo as string | undefined]
  }, [leftNavigation, vars])

  const leftNavDisplayItems = useNavDisplayItems(leftNavigation, leftMainActions, state)
  const [activeNavItem, activeSubNavItem] = useActiveItems(leftNavDisplayItems)

  usePendoIdentity(loggedInUser, stateRef)

  useEffect(() => {
    handleLogin(lite, main, vars, state?.loginFailed, oidc)
  }, [lite, vars, oidc, main, state?.loginFailed])

  const isReadyToRender = useReadyToRenderAndClearMask(state)
  if (!isReadyToRender) {
    return <LoadingPlaceholder />
  }

  return (
    <RouteViewer
      leftNavComponent={
        <LeftNav
          leftNavDisplayItems={leftNavDisplayItems.topItems}
          state={state}
          vars={vars}
          oidc={oidc}
          logo={logo}
          collapsedLogo={collapsedLogo}
          activeNavItem={activeNavItem}
          activeSubNavItem={activeSubNavItem}
        />
      }
      loggedInUser={loggedInUser}
      tenant={tenant}
      navigation={navRoutes}
      leftMainRoutes={leftMainRoutes}
      licenseInfo={licenseInfo}
      featuresInfo={featuresInfo}
      hideSideMenu={hideSideMenu}
      _showSideMenu={_showSideMenu}
      state={state}
      getState={getState}
      stateRef={stateRef}
      dispatch={dispatch}
      events={events}
      tokenInfo={tokenInfo}
      defaultRoute={getDefaultRoute()}
      RouteWrapper={vars.memo_route_component !== false ? MemoComponentWrapper : ComponentWrapper}
      vars={vars}
    />
  )
}

export default withRouter(Main)
